import React, { createRef } from 'react';
import classNames from 'classnames';
import { default as pluralize } from 'pluralize';
import { TransactionV2 } from '../transaction/transactionV2';
import { Transaction } from '../transaction/transaction';
import { ArtifactTypes } from '../../artifacts/artifact-types.lang';
import { objectUtils } from '@codametrix/ui-common';

type InvoiceState = {};

type InvoiceProps = {
  simplifiedInvoice: CMxCommonApp.SimplifiedInvoice;
  invoice?: AmplifyAPI.Invoice;
  processInstance?: AmplifyAPI.ProcessInstance;
  editable?: boolean;
  panelActivations?: CMxCommonApp.PanelActivations;
  handleCodeChange?: CMxCommonApp.CodeChangeHandler;
  handleCodeRemove?: CMxCommonApp.CodeChangeHandler;
  handleCodeSearchDrop?: CMxCommonApp.CodeChangeHandler;
  onChargeSessionAction?: CMxCommonApp.TransactionActionHandler;
  onCodeSearch?: CMxCommonApp.CodeSearchHandler;
  onCodeSearchSelection?: CMxCommonApp.CodeSearchSelectionHandler;
  onFeatureSelection?: (selection: CMxCommonApp.ChargeSessionSelection) => void;
  canDropOnto?: (
    item1: any,
    item2: any,
    position: string,
    allItems: any[]
  ) => boolean;
  onDropHandler?: (
    dropAction: CMxCommonApp.DroppedAction<CMxCommonApp.ChargeSessionCode>
  ) => void;
  transactionV2?: boolean;
};

class Invoice extends React.Component<InvoiceProps, InvoiceState> {
  static displayName = 'Invoice';
  static defaultProps = {
    autoFocus: false,
    searchResults: [],
    editable: true,
    panelActivations: {},
    onChargeSessionAction: (
      transactionAction: CMxCommonApp.TransactionAction
    ) => {},
    handleCodeChange: (change: CMxCommonApp.CodeChange) => {},
    handleCodeRemove: (change: CMxCommonApp.CodeChange) => {},
    handleCodeSearchDrop: (change: CMxCommonApp.CodeChange) => {},
    onCodeSearch: (codeQuery: CMxCommonApp.CodeQuery) => {},
    onCodeSearchSelection: (searchSelection: CMxCommonApp.CodeInfo) => {},
    onFeatureSelection: (feature: CMxCommonApp.ChargeSessionSelection) => {}
  };

  render(): React.ReactNode {
    const {
      simplifiedInvoice: invoice,
      panelActivations,
      handleCodeChange,
      handleCodeRemove,
      handleCodeSearchDrop,
      onCodeSearch,
      onCodeSearchSelection,
      onFeatureSelection,
      onChargeSessionAction,
      editable
    } = this.props;

    const { transactions } = invoice;
    const heading = pluralize('Procedure', transactions.length);
    const billingArea = invoice.billing_area ?? 'unspecified billing area';
    const billingProvider =
      invoice.billing_provider_name !== 'null'
        ? invoice.billing_provider_name
        : 'unspecified provider';
    let txnCount = 0;
    const txns = transactions.map((transaction, idx) => {
      if (transaction.type !== 'SHARED_CODES') {
        txnCount += 1;
      }
      const TransactionComponent = this.props.transactionV2
        ? TransactionV2
        : Transaction;
      const transactionProps = {
        onChargeSessionAction: onChargeSessionAction,
        panelActivations: panelActivations,
        handleCodeChange: handleCodeChange,
        handleCodeRemove: handleCodeRemove,
        handleCodeSearchDrop: handleCodeSearchDrop,
        onCodeSearch: onCodeSearch,
        onCodeSearchSelection: onCodeSearchSelection,
        onFeatureSelection: onFeatureSelection,
        lineNumber: txnCount,
        editable: editable,
        transaction: transaction
      };
      const tabContainerRef = createRef<HTMLDivElement>();

      //Only add scrollContainerRef if using TransactionV2
      const additionalProps = this.props.transactionV2
        ? { scrollContainerRef: tabContainerRef }
        : {};

      return (
        <div key={idx} className="invoice-here">
          <TransactionComponent {...additionalProps} {...transactionProps} />
        </div>
      );
    });

    const invoiceStyleClasses = [
      'invoice',
      `invoice-type-${invoice.type}`.toLowerCase(),
      {
        'invoice-no-transactions': transactions.length === 0,
        'invoice-no-billing-area': !invoice.billing_area
      }
    ];

    return (
      <div className={classNames(invoiceStyleClasses)}>
        <div className="invoice-provenance">
          {objectUtils.get(
            ArtifactTypes,
            invoice.type.toUpperCase(),
            invoice.type
          )}
        </div>
        <h4 className="invoice-heading">
          {heading} by {billingProvider}
          <span className="billing-area"> at {billingArea}</span>.
        </h4>
        {txns}
        <div className="no-charges empty-message">No codes available.</div>
      </div>
    );
  }
}

export { Invoice };
